import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import { DEEP_LINK_QUERY_KEYS } from '@/composables/jobs/deepLinkQuery';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { PROJECT_FILTER_QUERY_KEYS } from '@/core/shared/project/project-filter-query.const';
import { shouldStripQuery } from '@/router/should-strip-query.util';
import { buildTrackedPageVisit } from '@/router/tracked-page-visit.util';

export function globalBeforeEach(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  // redirect to /jobs on any /conversations route, preserving the tab in query
  if (to.path.startsWith('/conversations')) {
    // Extract the tab from the path (e.g., /conversations/resume -> resume)
    const tabMatch = to.path.match(/\/conversations\/?(resume|conversation|applications|notes)?/);
    const tab = tabMatch?.[1];

    // Add the tab to query params if it exists
    const query = tab ? { ...to.query, tab } : to.query;

    next({ path: '/jobs', params: to.params, query });
    return;
  }

  // Check if this is a navigation to the copilot activation page
  // If so, we want to preserve the route.query if it's valid
  const copilotActivationValidQueryParams = ['isCreatingJob', 'validate'];
  let isValidCopilotActivationRoute = false;
  if (to.name === 'jobs-copilot-activation') {
    isValidCopilotActivationRoute = Object.keys(to.query).every((key) =>
      copilotActivationValidQueryParams.includes(key),
    );
  }

  // Check if we have query params that should be preserved for jobs page
  const jobsQueryParams: readonly string[] = [
    ...DEEP_LINK_QUERY_KEYS,
    'applicationStatus',
    'tab',
    ...PROJECT_FILTER_QUERY_KEYS,
  ];
  const hasJobsQueryParams =
    to.path.startsWith('/jobs') &&
    Object.keys(to.query).some((key) => jobsQueryParams.includes(key));

  // Clear URL parameters when navigating between pages to prevent parameter leakage,
  // but preserve them for jobs page navigation and copilot activation
  if (shouldStripQuery(to, from, { hasJobsQueryParams, isValidCopilotActivationRoute })) {
    next({ path: to.path, hash: to.hash });
    return;
  }

  TrackingService.instance.registerPageVisit(buildTrackedPageVisit(to, location.origin));

  next();
}
